import {type ReactNode, useEffect, useId, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import {currency, digit} from 'src/feature/bite/helpers/currency'
import {useGetAnalytics} from 'src/feature/analytics/context/useGetAnalytics'
import './index.css'
import AverageAnalytics from 'src/feature/analytics/component/sections/AverageAnalytics'
import RateAnalytics from 'src/feature/analytics/component/sections/RateAnalytics'
import {LineAreaChart} from 'src/feature/analytics/component/components'
import {dataToSeries} from 'src/feature/analytics/utility'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import PillBar from '../component/components/pillBar'
import MCLogoFooter from '../assets/mastercard-black.svg'
import {useAuthUser} from 'react-auth-kit'

type Metric = {
  label: string
  value: string
  delta: string
  trend: 'up' | 'down'
  color: string
  tooltip?: string
  spark: number[]
}

type SectionHeaderProps = {
  eyebrow?: string
  title: string
  hint?: string
  action?: ReactNode
}

type ActionCardProps = {
  icon: string
  title: string
  text: string
  cta: string
  badge?: string
  colorClass: string
  onClick: () => void
}

type FinanceCardProps = {
  icon: string
  title: string
  text: string
  badge: string
  meta: string
  cta: string
  badgeClass: string
  colorClass: string
  onClick: () => void
}

const INSIGHT_SLIDER_INTERVAL = 7000

const getWrappedIndex = (currentIndex: number, step: number, itemCount: number) =>
  (currentIndex + step + itemCount) % itemCount

const chartValues = (items?: {date: string; value: number}[]) => {
  const values = items?.map((item) => item.value).filter((value) => Number.isFinite(value))

  if (!values || values.length < 2) {
    return [8, 12, 10, 15, 18, 22, 26]
  }

  return values.slice(-8)
}

function Sparkline({data, color, height = 34}: {data: number[]; color: string; height?: number}) {
  const uid = useId().replace(/:/g, '')
  const width = 120
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const step = data.length > 1 ? width / (data.length - 1) : width
  const points = data.map(
    (value, index) => [index * step, height - ((value - min) / range) * (height - 6) - 3] as const
  )
  const line = points
    .map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ')
  const area = `${line} L${width},${height} L0,${height} Z`

  return (
    <svg
      className='d-block w-100 mt-2'
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio='none'
      aria-hidden='true'
    >
      <defs>
        <linearGradient id={`dashboard-spark-${uid}`} x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor={color} stopOpacity='0.2' />
          <stop offset='100%' stopColor={color} stopOpacity='0' />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#dashboard-spark-${uid})`} />
      <path
        d={line}
        fill='none'
        stroke={color}
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function SectionHeader({eyebrow, title, hint, action}: SectionHeaderProps) {
  return (
    <div className='d-flex align-items-end justify-content-between gap-4 flex-wrap mb-4'>
      <div>
        {eyebrow && (
          <div className='dashboard-apple-eyebrow text-uppercase fw-semibold mb-1'>{eyebrow}</div>
        )}
        <h2 className='fs-2 fw-bold text-gray-900 mb-0'>{title}</h2>
        {hint && <p className='fs-7  mb-0 mt-1'>{hint}</p>}
      </div>
      {action}
    </div>
  )
}

function MetricCell({metric, isLast}: {metric: Metric; isLast: boolean}) {
  return (
    <div className={`dashboard-apple-metric p-6 ${isLast ? '' : 'border-end border-gray-200'}`}>
      <div className='d-flex align-items-center gap-2 fs-7 fw-semibold '>
        <span>{metric.label}</span>
        {metric.tooltip && (
          <i
            className='bi bi-info-circle text-gray-500'
            title={metric.tooltip}
            aria-label={metric.tooltip}
          ></i>
        )}
      </div>
      <div className='d-flex align-items-baseline gap-3 flex-wrap mt-3'>
        <span className='dashboard-apple-metric-value fw-bold text-gray-900'>{metric.value}</span>
        <span
          className={`d-inline-flex align-items-center gap-1 fs-8 fw-bold ${
            metric.trend === 'up' ? 'text-success' : 'text-danger'
          }`}
        >
          <i
            className={`bi ${metric.trend === 'up' ? 'bi-arrow-up-right' : 'bi-arrow-down-right'}`}
          ></i>
          {metric.delta}
        </span>
      </div>
      <Sparkline data={metric.spark} color={metric.color} />
    </div>
  )
}

function ActionCard({icon, title, text, cta, badge, colorClass, onClick}: ActionCardProps) {
  return (
    <button
      type='button'
      className='card dashboard-apple-hover h-100 text-start border border-gray-200 bg-white'
      onClick={onClick}
    >
      <div className='card-body p-6 d-flex flex-column h-100'>
        <div className='d-flex align-items-start justify-content-between gap-3 mb-5'>
          <span
            className={`dashboard-apple-card-icon ${colorClass}`}
            style={{backgroundColor: '#F3F0EE'}}
          >
            <i className={`bi ${icon} text-secondary`}></i>
          </span>
          {badge && <span className='border-bottom border-2 border-secondary'>{badge}</span>}
        </div>
        <h3 className='fs-4 fw-bold text-gray-900 mb-2'>{title}</h3>
        <p className='fs-7  mb-6 flex-grow-1'>{text}</p>
        <span className='d-inline-flex align-items-center gap-2 text-primary fw-bold fs-5 text-secondary'>
          {cta}
          <i className='bi bi-arrow-right text-secondary'></i>
        </span>
      </div>
    </button>
  )
}

function CampaignRow({
  status,
  name,
  meta,
  tag,
  stat,
  tagClass,
}: {
  status: 'active' | 'expiring'
  name: string
  meta: string
  tag: string
  stat: string
  tagClass: string
}) {
  return (
    <tr className='py-4 '>
      <td className='py-4 campaign-status-cell'>
        {status === 'active' ? (
          <i className='bi bi-check-circle-fill text-success fs-3'></i>
        ) : (
          <i className='bi bi-hourglass-bottom text-info fs-3'></i>
        )}
      </td>

      <td className='py-4 campaign-name-cell'>
        <div className='fs-6 fw-bold text-gray-900 text-truncate'>{name}</div>
        <div className='fs-8 '>{meta}</div>
      </td>

      <td className='py-4 campaign-type-cell'>
        <span className='campaign-type-badge bg-primary text-white p-2 rounded text-center'>
          {tag}
        </span>
      </td>

      <td className='py-4 campaign-product-cell'>
        <span>All Products</span>
      </td>

      <td className='py-4 campaign-customer-cell'>
        <span>All Members</span>
      </td>

      <td className='py-4 campaign-detail-cell'>
        <a href='#'>Details</a>
      </td>
    </tr>
  )
}

function CampaignRow2({
  status,
  name,
  meta,
  tag,
  stat,
  tagClass,
}: {
  status: 'active' | 'expiring'
  name: string
  meta: string
  tag: string
  stat: string
  tagClass: string
}) {
  return (
    <tr className='py-4 '>
      <td className='py-4 campaign-status-cell'>
        {status === 'active' ? (
          <i className='bi bi-check-circle-fill text-success fs-3'></i>
        ) : (
          <i className='bi bi-hourglass-bottom text-info fs-3'></i>
        )}
      </td>

      <td className='py-4 campaign-name-cell'>
        <div className='fs-6 fw-bold text-gray-900 text-truncate'>{name}</div>
        <div className='fs-8 '>{meta}</div>
      </td>

      <td className='py-4 campaign-type-cell'>
        <span className='campaign-type-badge bg-primary text-white p-2 rounded text-center'>
          {tag}
        </span>
      </td>

      <td className='py-4 campaign-product-cell'>
        <span>All Products</span>
      </td>

      <td className='py-4 campaign-customer-cell'>
        <span>Selected Members</span>
      </td>

      <td className='py-4 campaign-detail-cell'>
        <a href='#'>Detail</a>
      </td>
    </tr>
  )
}

function CampaignRow3({
  status,
  name,
  meta,
  tag,
  stat,
  tagClass,
}: {
  status: 'active' | 'expiring'
  name: string
  meta: string
  tag: string
  stat: string
  tagClass: string
}) {
  return (
    <tr className='py-4 '>
      <td className='py-4 campaign-status-cell'>
        {status === 'active' ? (
          <i className='bi bi-check-circle-fill text-success fs-3'></i>
        ) : (
          <i className='bi bi-hourglass-bottom text-info fs-3'></i>
        )}
      </td>

      <td className='py-4 campaign-name-cell'>
        <div className='fs-6 fw-bold text-gray-900 text-truncate'>{name}</div>
        <div className='fs-8 '>{meta}</div>
      </td>

      <td className='py-4 campaign-type-cell'>
        <span className='campaign-type-badge bg-primary text-white p-2 rounded text-center'>
          {tag}
        </span>
      </td>

      <td className='py-4 campaign-product-cell'>
        <span>Selected Products</span>
      </td>

      <td className='py-4 campaign-customer-cell'>
        <span>All Members</span>
      </td>

      <td className='py-4 campaign-detail-cell'>
        <a href='#'>Detail</a>
      </td>
    </tr>
  )
}

function SetupChecklist({onFinishSetup}: {onFinishSetup: () => void}) {
  const [done, setDone] = useState<Record<string, boolean>>({
    business: true,
    products: false,
    rules: true,
    signup: false,
    referrals: false,
    vip: false,
  })

  const items = [
    {id: 'business', label: 'Add business details', meta: 'Name, address, hours'},
    {
      id: 'products',
      label: 'Add your products',
      meta: 'Menu or catalog with pricing',
      to: '/products',
    },
    {id: 'rules', label: 'Set how customers earn', meta: 'Points per dollar'},
    {
      id: 'signup',
      label: 'Activate your new customer reward',
      meta: 'Give first-timers a reason to come back',
      to: '/campaign',
    },
    {
      id: 'referrals',
      label: 'Turn on referrals',
      meta: 'Reward customers who bring new ones in',
      to: '/referral',
    },
  ]

  const completed = items.filter((item) => done[item.id]).length
  const progress = Math.round((completed / items.length) * 100)
  const navigate = useNavigate()

  return (
    <div className='card h-100 border border-gray-200 shadow-sm'>
      <div className='card-body p-7 d-flex flex-column'>
        <div className='d-flex align-items-start justify-content-between gap-4 mb-5'>
          <div>
            <div className='dashboard-apple-eyebrow text-uppercase fw-semibold mb-1'>Get ready</div>
            <h3 className='fs-3 fw-bold text-gray-900 mb-0'>Program setup</h3>
          </div>
          <span className='fw-bold border-bottom border-2 border-secondary'>
            {completed} / {items.length} complete
          </span>
        </div>

        <div className='mb-6'>
          {items.map((item, index) => (
            <PillBar isDone={done[item.id]} index={index} />
          ))}
        </div>

        <div className='d-flex flex-column flex-grow-1'>
          {items.map((item, index) => {
            const isDone = done[item.id]

            return (
              <button
                key={item.id}
                type='button'
                className='dashboard-apple-check-item d-grid align-items-center gap-4 text-start bg-transparent border-0 px-0 py-3 border-bottom border-gray-200'
                onClick={() => {
                  if (item.to) {
                    navigate(item.to)
                  } else return
                }}
              >
                <h2
                  className={`d-inline-flex align-items-center justify-content-center text-gray-700 ${
                    isDone ? 'is-done' : ''
                  }`}
                >
                  {index + 1}
                </h2>
                <span className='min-w-0'>
                  <span
                    className={`d-block fs-5 fw-bold ${
                      isDone ? ' text-decoration-line-through' : 'text-gray-900'
                    }`}
                  >
                    {item.label}
                  </span>
                  <span className='d-block fs-5 '>{item.meta}</span>
                </span>
              </button>
            )
          })}
        </div>

        <button
          type='button'
          className='btn btn-primary mt-6'
          onClick={onFinishSetup}
          style={{
            width: '185px',
          }}
        >
          Finish setup <i className='bi bi-arrow-right ms-2'></i>
        </button>
      </div>
    </div>
  )
}

function FinanceOfferCard({
  icon,
  title,
  text,
  badge,
  meta,
  cta,
  badgeClass,
  colorClass,
  onClick,
}: FinanceCardProps) {
  return (
    <button
      type='button'
      className='card dashboard-apple-hover h-100 text-start border border-gray-200 bg-white'
      onClick={onClick}
    >
      <div className='card-body p-6 d-flex flex-column h-100'>
        <div className='d-flex align-items-start justify-content-between gap-3 mb-5'>
          <span className={`dashboard-apple-card-icon`} style={{backgroundColor: '#F3F0EE'}}>
            <i className={`bi ${icon}  text-secondary`}></i>
          </span>
          <span className={`border-bottom border-2 border-secondary`}>{badge}</span>
        </div>
        <h3 className='fs-4 fw-bold text-gray-900 mb-2'>{title}</h3>
        <p className='fs-7  mb-5 flex-grow-1'>{text}</p>
        <div className='fs-8  mb-4'>
          <i className='bi bi-shield-check me-2'></i>
          {meta}
        </div>
        <span className='d-inline-flex align-items-center gap-2 text-primary fw-bold fs-5 text-secondary'>
          {cta}
          <i className='bi bi-arrow-right text-secondary'></i>
        </span>
      </div>
    </button>
  )
}

export default function DashboardPage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const [period, setPeriod] = useState(3)
  const [insightIndex, setInsightIndex] = useState(0)
  const [insightTopIndex, setInsightTopIndex] = useState(0)
  const {data, isLoading} = useGetAnalytics(period)

  const selectedPeriodText = useMemo(() => {
    switch (period) {
      case 1:
        return intl.formatMessage({id: 'GLOBAL.IN_TODAY'})
      case 3:
        return intl.formatMessage({id: 'GLOBAL.IN_LAST_WEEK'})
      case 4:
        return intl.formatMessage({id: 'GLOBAL.IN_LAST_30_DAYS'})
      case 5:
        return intl.formatMessage({id: 'GLOBAL.IN_LAST_90_DAYS'})
      default:
        return ''
    }
  }, [intl, period])

  const metrics = useMemo<Metric[]>(
    () => [
      {
        label: intl.formatMessage({id: 'ANALYTICS.LOYALTY_MEMBER'}),
        value: digit(data?.member?.loyaltyMemberCount || 0),
        delta: '+48',
        trend: 'up',
        color: '#141413',
        tooltip: intl.formatMessage({id: 'ANALYTICS.LOYALTY_MEMBER_TOOLTIP'}),
        spark: chartValues(data?.member?.loyaltyMemberChartData),
      },
      {
        label: intl.formatMessage({id: 'ANALYTICS.NEW_MEMBERS_JOINED'}),
        value: digit(data?.member?.newMembersJoinedCount || 0),
        delta: '+5%',
        trend: 'up',
        color: '#3b82f6',
        tooltip: intl.formatMessage(
          {id: 'ANALYTICS.ACTIVE_MEMBER_TOOLTIP'},
          {time: selectedPeriodText}
        ),
        spark: chartValues(data?.member?.activeMemberChartData),
      },
      {
        label: intl.formatMessage({id: 'ANALYTICS.AVG_ORDER_SIZE'}),
        value: currency(data?.average?.averageOrderSize || 0),
        delta: '+3%',
        trend: 'up',
        color: '#f37338',
        tooltip: intl.formatMessage(
          {id: 'ANALYTICS.AVG_ORDER_SIZE_TOOLTIP'},
          {time: selectedPeriodText}
        ),
        spark: chartValues(data?.revenue?.totalRevenueChartData),
      },
    ],
    [data, intl, period, selectedPeriodText]
  )

  const insightsTop = useMemo(
    () => [
      {
        title: 'Members make a purchase every 15 days on average',
        meta: 'A well-timed reminder around day 12 can shorten the gap between visits.',
      },
      {
        title: `${intl.formatMessage({id: 'DASHBOARD.INFO.TEXT.1'})} ${intl.formatMessage({
          id: 'DASHBOARD.INFO.TEXT.2',
        })}`,
        meta: `${intl.formatMessage({id: 'DASHBOARD.INFO.TEXT.3'})}`,
      },
    ],
    [intl]
  )

  const insights = useMemo(
    () => [
      {
        title: 'Drive Repeat Engagement with Point Campaigns',
        meta: 'Reward customers for their activity, encourage repeat purchases, and create ongoing engagement across every interaction.',
      },
      {
        title: 'Boost Sales with Discount Campaigns',
        meta: 'Create purchase urgency, increase conversion rates, and drive incremental revenue through compelling offers.',
      },
      {
        title: `Convert Intent with Coupon Campaigns`,
        meta: `Deliver targeted incentives that motivate action, improve redemption rates, and accelerate customer purchases.`,
      },
      {
        title: `Build Loyalty with Birthday Campaigns`,
        meta: `Celebrate customers with personalized rewards, strengthen emotional connections, and increase brand affinity.`,
      },
      {
        title: `Grow Through Advocacy with Referral Campaigns`,
        meta: `Encourage customer recommendations, acquire new members efficiently, and expand your loyal customer base.`,
      },
      {
        title: 'Drive Repeat Engagement with Point Campaigns',
        meta: 'Reward customers for their activity, encourage repeat purchases, and create ongoing engagement across every interaction.',
      },
    ],
    [intl]
  )

  const goToPreviousInsightTop = () => {
    setInsightTopIndex((current) => getWrappedIndex(current, -1, insightsTop.length))
  }

  const goToNextInsightTop = () => {
    setInsightTopIndex((current) => getWrappedIndex(current, 1, insightsTop.length))
  }

  const goToPreviousInsight = () => {
    setInsightIndex((current) => getWrappedIndex(current, -1, insights.length))
  }

  const goToNextInsight = () => {
    setInsightIndex((current) => getWrappedIndex(current, 1, insights.length))
  }

  const nextInsightIndex = getWrappedIndex(insightIndex, 1, insights.length)

  useEffect(() => {
    const timer = window.setInterval(() => {
      setInsightIndex((current) => (current + 1) % insights.length)
    }, INSIGHT_SLIDER_INTERVAL)

    return () => window.clearInterval(timer)
  }, [insights.length])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setInsightTopIndex((current) => (current + 1) % insightsTop.length)
    }, INSIGHT_SLIDER_INTERVAL)

    return () => window.clearInterval(timer)
  }, [insightsTop.length])

  const periodText = () => {
    switch (period) {
      case 1:
        return intl.formatMessage({id: 'GLOBAL.IN_TODAY'})
      case 3:
        return intl.formatMessage({id: 'GLOBAL.IN_LAST_WEEK'})
      case 4:
        return intl.formatMessage({id: 'GLOBAL.IN_LAST_30_DAYS'})
      case 5:
        return intl.formatMessage({id: 'GLOBAL.IN_LAST_90_DAYS'})
      default:
        return ''
    }
  }

  const authUser = useAuthUser()

  return (
    <div className='dashboard-apple-page'>
      <div className='d-flex align-items-end justify-content-between gap-5 flex-wrap'>
        <div>
          <h1 className='fs-1 fw-bolder text-gray-900 mb-2'>Welcome, Cup of Joy</h1>
          <p className='fs-6  mb-0'>Here's where things stand</p>
        </div>
        <div className='d-flex align-items-center gap-3 flex-wrap'>
          <button type='button' className='btn btn-primary' onClick={() => navigate('/campaign')}>
            <i className='bi bi-plus-lg me-2'></i>
            Quick action
          </button>
        </div>
      </div>

      <section className='mt-10'>
        <SectionHeader
          eyebrow={`YOUR BUSINESS AT A GLANCE`}
          title='Snapshot'
          action={
            <button
              type='button'
              className='btn btn-light btn-sm'
              disabled={isLoading}
              onClick={() => setPeriod(3)}
            >
              {intl.formatMessage({id: 'GLOBAL.LAST_WEEK'})}
              <i className='bi bi-chevron-down ms-2'></i>
            </button>
          }
        />

        <div
          className='dashboard-apple-action-grid'
          style={{
            gridTemplateColumns: '1fr 1fr 1fr',
          }}
        >
          <div className='card border border-gray-200 shadow-sm overflow-hidden'>
            <div className='w-100 p-5'>
              <div className='row'>
                <div className='col-8 text-secondary fs-2 fw-bold'>
                  {data?.member?.loyaltyMemberCount || 0}
                </div>

                <div className='col-8 fs-2 position-relative'>
                  {intl.formatMessage({id: 'ANALYTICS.LOYALTY_MEMBER'})}
                  <span className='position-relative z-50'>
                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip>
                          {intl.formatMessage(
                            {id: 'ANALYTICS.LOYALTY_MEMBER_TOOLTIP'},
                            {time: period}
                          )}{' '}
                          {intl.formatMessage({id: 'REAL_UPDATE'})}
                        </Tooltip>
                      }
                    >
                      <i className='bi bi-info-circle text-primary ms-1'></i>
                    </OverlayTrigger>
                  </span>
                </div>
              </div>
            </div>
            <div className='position-relative w-100 h-100'>
              <LineAreaChart
                data={dataToSeries(
                  intl.formatMessage({id: 'ANALYTICS.LOYALTY_MEMBER'}),
                  data?.member?.loyaltyMemberChartData || []
                )}
              />
            </div>
          </div>

          <div className='card border border-gray-200 shadow-sm overflow-hidden'>
            <div className='w-100 p-5'>
              <div className='row'>
                <div className='col-12 text-secondary fs-2 fw-bold'>
                  {data?.member?.activeMemberCount || 0}
                </div>
                <div className='col-12 fs-2'>
                  {intl.formatMessage({id: 'ANALYTICS.ACTIVE_MEMBER'})}
                  <span className='position-relative'>
                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip>
                          {intl.formatMessage(
                            {id: 'ANALYTICS.ACTIVE_MEMBER_TOOLTIP'},
                            {time: period}
                          )}{' '}
                          {intl.formatMessage({id: 'DAILY_UPDATE'})}
                        </Tooltip>
                      }
                    >
                      <i className='bi bi-info-circle text-primary ms-1'></i>
                    </OverlayTrigger>
                  </span>
                </div>
              </div>
            </div>
            <div className='position-relative w-100 h-100'>
              <LineAreaChart
                data={dataToSeries(
                  intl.formatMessage({id: 'ANALYTICS.ACTIVE_MEMBER'}),
                  data?.member?.activeMemberChartData || []
                )}
              />
            </div>
          </div>

          <div className='card border border-gray-200 shadow-sm overflow-hidden'>
            <div className='w-100 p-5'>
              <div className='row'>
                <div className='col-4 text-secondary fs-2 fw-bold'>
                  {currency(data?.average?.averageOrderSize || 0)}
                </div>
                <div className='fs-2'>
                  {intl.formatMessage({id: 'ANALYTICS.AVG_ORDER_SIZE'})}
                  <span className='position-relative'>
                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip>
                          {intl.formatMessage(
                            {id: 'ANALYTICS.AVG_ORDER_SIZE_TOOLTIP'},
                            {time: period}
                          )}{' '}
                          {intl.formatMessage({id: 'REAL_UPDATE'})}
                        </Tooltip>
                      }
                    >
                      <i className='bi bi-info-circle text-primary ms-1'></i>
                    </OverlayTrigger>
                  </span>
                </div>
              </div>
            </div>
            <div className='position-relative w-100 h-100'>
              <LineAreaChart
                data={dataToSeries(
                  intl.formatMessage({id: 'ANALYTICS.LOYALTY_MEMBER'}),
                  data?.member?.loyaltyMemberChartData || []
                )}
              />
            </div>
          </div>
        </div>

        <div className='p-8 rounded bg-light d-flex align-items-center justify-content-between gap-4 flex-wrap mt-10'>
          <div className='d-flex align-items-center gap-4 min-w-0'>
            <span
              className='dashboard-apple-insight-icon'
              style={{
                backgroundColor: '#FDEADA',
                boxShadow: 'none',
                width: 48,
                height: 48,
              }}
            >
              <i className='bi bi-rocket-takeoff text-secondary fs-1'></i>
            </span>
            <div key={insightTopIndex} className='dashboard-apple-insight-copy min-w-0'>
              <div className='fs-4 fw-bold text-gray-900'>{insightsTop[insightTopIndex].title}</div>
              <div className='fs-5  mt-1'>{insightsTop[insightTopIndex].meta}</div>
            </div>
          </div>
          <div className='d-flex align-items-center gap-2 pe-12'>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light'
              aria-label='Previous top insight'
              onClick={goToPreviousInsightTop}
            >
              <i className='bi bi-chevron-left fs-1 text-secondary'></i>
            </button>

            <button
              type='button'
              className='btn btn-icon btn-sm btn-light'
              aria-label='Next top insight'
              onClick={goToNextInsightTop}
            >
              <i className='bi bi-chevron-right fs-1 text-secondary'></i>
            </button>
            {/*  {insightsTop.map((insight, index) => (
              <button
                key={insight.title}
                type='button'
                className={`border-0 rounded-circle ${
                  index === insightTopIndex ? 'bg-primary' : 'bg-gray-400'
                }`}
                style={{width: 8, height: 8, padding: 0}}
                aria-label={`Insight ${index + 1}`}
                onClick={() => setInsightTopIndex(index)}
              />
            ))} */}
          </div>
        </div>
      </section>

      <section className='mt-10'>
        <SectionHeader eyebrow='YOUR NEXT MOVE' title='What do you want to accomplish?' hint='' />
        <div
          className='dashboard-apple-action-grid'
          style={{
            gridTemplateColumns: '1fr 1fr 1fr',
          }}
        >
          <ActionCard
            icon='bi-arrow-counterclockwise'
            title='Bring customers back'
            text='A discount campaign is the fastest way to get lapsed customers back in the door. They already know you — a small offer is usually all it takes.'
            cta='Create a discount campaign'
            badge='Recommended'
            colorClass='text-danger'
            onClick={() => navigate('/campaign')}
          />
          <ActionCard
            icon='bi-graph-up-arrow'
            title='Reward your regulars'
            text='A points campaign gives your best customers a reason to keep coming back — and spend a little more each time they do.'
            cta='Create a points campaign'
            colorClass='text-primary'
            onClick={() => navigate('/campaign')}
          />
          <ActionCard
            icon='bi-people'
            title='Grow through referrals'
            text='Your happiest customers are your best recruiters. They refer someone, they earn. New customers get rewarded just for showing up.'
            cta='Set up referrals'
            colorClass='text-success'
            onClick={() => navigate('/referral')}
          />
        </div>
      </section>

      <div className='dashboard-apple-bottom-grid mt-10' style={{gridTemplateColumns: '1fr 2fr'}}>
        <SetupChecklist onFinishSetup={() => navigate('/products')} />

        <section className='card'>
          <div className='card-body overflow-hidden'>
            <div className='d-flex align-items-start justify-content-between gap-4 mb-5'>
              <div>
                <div className='dashboard-apple-eyebrow text-uppercase fw-semibold mb-1'>
                  Running now
                </div>
                <h3 className='fs-3 fw-bold text-gray-900 mb-0'>Latest Campaigns</h3>
              </div>
              <button
                type='button'
                className='btn btn-link fw-bold p-0 text-secondary'
                onClick={() => navigate('/campaign')}
              >
                View all <i className='bi bi-arrow-right ms-1 text-secondary'></i>
              </button>
            </div>

            <table className='campaign-running-table align-items-center border-top border-gray-200 w-100'>
              <CampaignRow
                status='expiring'
                name='Happy Hour Bonus'
                meta='#1900935922 - ends soon'
                tag='Discount'
                stat='142 uses'
                tagClass='badge-light-danger'
              />
              <CampaignRow2
                status='active'
                name='Referrals'
                meta='#2683195810 - ongoing'
                tag='Referrals'
                stat='28 referrals'
                tagClass='badge-light-primary'
              />
              <CampaignRow3
                status='active'
                name='Birthday reward'
                meta='#5012984471 - ongoing'
                tag='Birthday'
                stat='12 this month'
                tagClass='badge-light-warning'
              />
            </table>

            <div className='rounded d-flex align-items-center justify-content-between gap-4 flex-wrap mt-12'>
              <div className='d-flex gap-6 justify-content-center'>
                <div
                  className='d-flex align-items-center gap-4 min-w-0  p-8'
                  style={{
                    borderRadius: '15px',
                    backgroundColor: '#fdeada',
                  }}
                >
                  <div key={insightIndex} className='dashboard-apple-insight-copy min-w-0'>
                    <div className='fs-4 fw-bold text-secondary'>
                      {insights[insightIndex].title}
                    </div>
                    <div className='fs-6  mt-1'>{insights[insightIndex].meta}</div>
                  </div>
                </div>

                <div
                  className='d-flex align-items-center gap-4 min-w-0 p-8'
                  style={{
                    borderRadius: '15px',
                    backgroundColor: '#fdeada',
                  }}
                >
                  <div key={nextInsightIndex} className='dashboard-apple-insight-copy min-w-0'>
                    <div className='fs-4 fw-bold text-secondary'>
                      {insights[nextInsightIndex].title}
                    </div>
                    <div className='fs-6  mt-1'>{insights[nextInsightIndex].meta}</div>
                  </div>
                </div>
              </div>
              <div className='d-flex align-items-center gap-6 mt-8 justify-content-center w-100'>
                <button
                  type='button'
                  className='btn btn-icon btn-sm'
                  aria-label='Previous insight'
                  onClick={goToPreviousInsight}
                >
                  <i className='bi bi-chevron-left fs-1 text-secondary'></i>
                </button>

                <button
                  type='button'
                  className='btn btn-icon btn-sm'
                  aria-label='Next insight'
                  onClick={goToNextInsight}
                >
                  <i className='bi bi-chevron-right fs-1 text-secondary'></i>
                </button>

                {/* {insights.map((insight, index) => (
                  <button
                    key={insight.title}
                    type='button'
                    className={`border-0 rounded-circle ${
                      index === insightIndex ? 'bg-primary' : 'bg-gray-400'
                    }`}
                    style={{width: 8, height: 8, padding: 0}}
                    aria-label={`Insight ${index + 1}`}
                    onClick={() => setInsightIndex(index)}
                  />
                ))} */}
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className='mt-10 bg-primary px-8 py-5 text-white rounded gap-8'>
        <div className='d-flex justify-content-between align-items-center'>
          <div className=''>
            <h3 className='text-white fs-1 m-0'>
              <span className='text-secondary'>Need help?</span>
            </h3>
            <p className='text-white fs-3 m-0'>
              How-to guides, answers to common questions, and direct support — all in one place.
            </p>
          </div>
          <div>
            <a href='https://docs.everyvisit.com/' target='blank' className='btn btn-ghost'>
              Visit Help Center
              <i className='bi bi-arrow-right fs-3 ps-2'></i>
            </a>
          </div>
        </div>
      </section>

      <section className='mt-10'>
        <SectionHeader
          eyebrow='Financial services'
          title='Financing built for your business'
          hint='Browse and compare offers from leading providers without affecting your credit score.'
          action={
            <button
              type='button'
              className='btn btn-link fw-bold p-0 text-secondary'
              onClick={() => navigate('/finance')}
            >
              All offers <i className='bi bi-arrow-right ms-1 text-secondary'></i>
            </button>
          }
        />
        <div
          className='dashboard-apple-action-grid'
          style={{
            gridTemplateColumns: '1fr 1fr 1fr',
          }}
        >
          <FinanceOfferCard
            icon='bi-credit-card-2-front'
            title='Business Credit Cards'
            text='Compare business credit card offers from leading issuers. Find options with cashback, rewards, or low rates — based on what matters to your business.'
            badge='3 offers'
            meta='Offers from top issuers'
            cta='Compare options'
            badgeClass='badge-primary'
            colorClass='bg-dark text-primary'
            onClick={() => navigate('/finance/business-card')}
          />
          <FinanceOfferCard
            icon='bi-cash-stack'
            title='Business Loans'
            text='Browse loan offers from multiple lenders in one place. See rates and estimated repayments before you apply — no commitment required.'
            badge='Multiple lenders'
            meta='Terms set by each lender'
            cta='Compare options'
            badgeClass='badge-light-success'
            colorClass='bg-light-success text-success'
            onClick={() => navigate('/finance/business-loan')}
          />
          <FinanceOfferCard
            icon='bi-shield-shaded'
            title='Business Insurance'
            text='Compare business insurance options from licensed providers. Liability, property, and business coverage — side by side.'
            badge='4 providers'
            meta='Quotes from licensed carriers'
            cta='Compare options'
            badgeClass='badge-light-warning'
            colorClass='bg-light-warning text-warning'
            onClick={() => navigate('/finance/insurance')}
          />
        </div>
      </section>

      {/*      <section className='mt-10 bg-primary px-8 py-5 text-white rounded gap-8'>
        <div className='d-flex justify-content-between align-items-center mb-8'>
          <div className=''>
            <h3 className='text-white fs-1 m-0'>
              Need a hand getting <span className='text-secondary'>set up?</span>
            </h3>
            <p className='text-white fs-3 m-0'>
              Guides and answers to help you launch rewards and campaigns with confidence.
            </p>
          </div>
          <div>
            <button className='btn btn-ghost'>
              Visit Help Center
              <i className='bi bi-arrow-right fs-3 ps-2'></i>
            </button>
          </div>
        </div>

        <div className='d-flex align-items-center gap-5 text-white py-8 border-top'>
          <a className='text-white' href='#'>
            Contact Us
          </a>
          <a className='text-white' href='#'>
            About everyvisit
          </a>

          <a className='text-white' href='#'>
            mastercard.com
          </a>
          <a className='text-white' href='#'>
            Terms of Use
          </a>

          <a className='text-white' href='#'>
            Privacy Notice
          </a>

          <a className='text-white' href='#'>
            Accessibility Statement
          </a>
        </div>

        <div className='pb-8'>
          <p>
            Mastercard and everyvisit are registered trademarks, and the circles design is a
            trademark of Mastercard International Incorporated. ©2026 Mastercard
          </p>
        </div>
      </section> */}
    </div>
  )
}
